/**
 * Defense Technology Intelligence graph canvas.
 *
 * Renders the fused Publications + PLA Contracts graph with
 * react-force-graph-2d. Supports cluster-first rendering (category nodes
 * with counts), single-click selection, double-click expansion, hover
 * tooltips, a right-click context menu, zoom/pan/fit controls, and
 * dashed rendering for derived relationships so inferred links are never
 * presented as directly observed ones.
 *
 * Spatial context is preserved on expansion: known node positions are
 * re-seeded on every data change and new nodes spawn adjacent to the node
 * they were expanded from instead of the whole graph re-laying out.
 */
import {
  FC,
  MouseEvent as ReactMouseEvent,
  ComponentType,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  ForceGraphMethods,
  ForceGraphProps,
  LinkObject,
  NodeObject,
} from 'react-force-graph-2d';
import {
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { forceCollide } from 'd3';
import {
  IntelNodeType,
  IntelRelationshipType,
} from '../../types/intelligenceGraph';
import { NODE_TYPE_META, nodeRadius } from './nodeStyles';

type ForceGraph2DComponent = ComponentType<
  ForceGraphProps & { ref?: MutableRefObject<ForceGraphMethods | undefined> }
>;

// `next/dynamic` does not forward refs to the lazy-loaded component, which
// would leave imperative methods (zoom, zoomToFit, centerAt) unavailable.
// Loading the module directly on the client keeps the real forwardRef
// component so ref access works (same pattern as ExportControlVetting).
function useForceGraph2D(): ForceGraph2DComponent | null {
  const [component, setComponent] = useState<ForceGraph2DComponent | null>(
    null,
  );
  useEffect(() => {
    let mounted = true;
    import('react-force-graph-2d').then((mod) => {
      if (mounted) setComponent(() => mod.default as ForceGraph2DComponent);
    });
    return () => {
      mounted = false;
    };
  }, []);
  return component;
}

export interface RenderNode {
  id: string;
  kind: 'entity' | 'cluster';
  nodeType: IntelNodeType;
  label: string;
  /** Cluster member count (cluster nodes only). */
  count?: number;
  recordCount: number;
  connectionCount: number;
  evidenceCount: number;
  datasets: string[];
  isRoot?: boolean;
  /** Anchor node id near which this node should first appear. */
  anchorId?: string;
}

export interface RenderLink {
  source: string;
  target: string;
  edgeType: string;
  relationshipType: IntelRelationshipType;
  confidence: number;
  evidenceCount: number;
}

export interface ContextMenuActions {
  onExpand?: (nodeId: string) => void;
  onFocus?: (nodeId: string) => void;
  onHide?: (nodeId: string) => void;
  onFindConnections?: (nodeId: string) => void;
  onViewEvidence?: (nodeId: string) => void;
  onOpenSource?: (nodeId: string) => void;
}

interface IntelligenceGraphProps {
  nodes: RenderNode[];
  links: RenderLink[];
  width: number;
  height: number;
  selectedNodeId?: string | null;
  highlightedNodeIds?: Set<string>;
  onNodeSelect?: (nodeId: string | null) => void;
  onNodeExpand?: (nodeId: string) => void;
  onLinkSelect?: (link: RenderLink) => void;
  contextActions?: ContextMenuActions;
  /** Increment to trigger zoom-to-fit. */
  fitSignal?: number;
}

type FGNode = NodeObject & RenderNode & { x?: number; y?: number };
type FGLink = LinkObject & RenderLink;

const DOUBLE_CLICK_MS = 320;
const LABEL_ZOOM_THRESHOLD = 1.1;
/** Zoom level at which edge-type labels appear on selected/hovered links. */
const EDGE_LABEL_ZOOM = 1.3;
/**
 * Max label width in display units — CJK characters count double so long
 * Chinese titles truncate to a comparable rendered width as Latin ones.
 */
const MAX_LABEL_UNITS = 30;

function charUnits(ch: string): number {
  return /[\u1100-\u11FF\u2E80-\uA4CF\uAC00-\uD7A3\uF900-\uFAFF\uFE30-\uFE4F\uFF00-\uFFEF]/.test(
    ch,
  )
    ? 2
    : 1;
}

/** CJK-aware truncation so wide characters don't overflow neighbors. */
function truncate(label: string, maxUnits = MAX_LABEL_UNITS): string {
  let units = 0;
  for (let i = 0; i < label.length; i += 1) {
    units += charUnits(label[i]);
    if (units > maxUnits) return `${label.slice(0, i)}…`;
  }
  return label;
}

/** Approximate half-width of a node's rendered label, in graph units. */
function labelHalfWidth(label: string, fontSize: number): number {
  let units = 0;
  const shown = truncate(label);
  for (let i = 0; i < shown.length; i += 1) units += charUnits(shown[i]);
  return (units * fontSize * 0.5) / 2 + 2;
}

function datasetSummary(datasets: string[]): string {
  if (datasets.length > 1) return 'Publications + PLA Contracts';
  return datasets[0] === 'publications' ? 'Publications' : 'PLA Contracts';
}

function drawShape(
  ctx: CanvasRenderingContext2D,
  shape: 'circle' | 'square' | 'diamond' | 'hexagon',
  x: number,
  y: number,
  r: number,
) {
  ctx.beginPath();
  if (shape === 'circle') {
    ctx.arc(x, y, r, 0, 2 * Math.PI);
  } else if (shape === 'square') {
    const s = r * 0.9;
    ctx.rect(x - s, y - s, s * 2, s * 2);
  } else if (shape === 'diamond') {
    ctx.moveTo(x, y - r);
    ctx.lineTo(x + r, y);
    ctx.lineTo(x, y + r);
    ctx.lineTo(x - r, y);
    ctx.closePath();
  } else {
    for (let i = 0; i < 6; i += 1) {
      const angle = (Math.PI / 3) * i - Math.PI / 6;
      const px = x + r * Math.cos(angle);
      const py = y + r * Math.sin(angle);
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();
  }
}

export const IntelligenceGraph: FC<IntelligenceGraphProps> = ({
  nodes,
  links,
  width,
  height,
  selectedNodeId,
  highlightedNodeIds,
  onNodeSelect,
  onNodeExpand,
  onLinkSelect,
  contextActions,
  fitSignal,
}) => {
  const ForceGraph2D = useForceGraph2D();
  const graphRef = useRef<ForceGraphMethods>();
  const positionsRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  /** Manually pinned nodes (drag or context-menu pin) keep fixed positions. */
  const pinnedRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  const lastClickRef = useRef<{ id: string; time: number }>({
    id: '',
    time: 0,
  });
  const [hoverNode, setHoverNode] = useState<FGNode | null>(null);
  const [hoverLink, setHoverLink] = useState<FGLink | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1.5);
  const [pinnedVersion, setPinnedVersion] = useState(0);
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    nodeId: string;
  } | null>(null);

  /**
   * Builds graph data, seeding coordinates from remembered positions so
   * expansion doesn't reposition the whole graph, and spawning new nodes
   * around their anchor (the node that was expanded). Pinned nodes get
   * fx/fy so the simulation never fights a manual arrangement.
   */
  const graphData = useMemo(() => {
    const positions = positionsRef.current;
    const fgNodes: FGNode[] = nodes.map((node, index) => {
      const pinned = pinnedRef.current.get(node.id);
      if (pinned) {
        return {
          ...node,
          x: pinned.x,
          y: pinned.y,
          fx: pinned.x,
          fy: pinned.y,
        };
      }
      const known = positions.get(node.id);
      if (known) {
        return { ...node, x: known.x, y: known.y };
      }
      const anchor = node.anchorId ? positions.get(node.anchorId) : undefined;
      const angle = (index * 2 * Math.PI) / Math.max(nodes.length, 1);
      const spawnRadius = 60 + Math.random() * 40;
      return {
        ...node,
        x: (anchor?.x ?? 0) + spawnRadius * Math.cos(angle),
        y: (anchor?.y ?? 0) + spawnRadius * Math.sin(angle),
      };
    });
    const ids = new Set(fgNodes.map((n) => n.id));
    const fgLinks: FGLink[] = links
      .filter((l) => ids.has(l.source) && ids.has(l.target))
      .map((l) => ({ ...l }));
    return { nodes: fgNodes, links: fgLinks };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, links, pinnedVersion]);

  /** Selected node's direct neighbors + connected link keys for emphasis. */
  const { neighborIds, connectedLinkKeys } = useMemo(() => {
    const nIds = new Set<string>();
    const lKeys = new Set<string>();
    if (selectedNodeId) {
      links.forEach((l) => {
        if (l.source === selectedNodeId || l.target === selectedNodeId) {
          nIds.add(l.source);
          nIds.add(l.target);
          lKeys.add(`${l.source}|${l.edgeType}|${l.target}`);
        }
      });
    }
    return { neighborIds: nIds, connectedLinkKeys: lKeys };
  }, [links, selectedNodeId]);

  const linkKey = (link: FGLink): string => {
    const s =
      typeof link.source === 'object'
        ? String((link.source as NodeObject).id)
        : String(link.source);
    const t =
      typeof link.target === 'object'
        ? String((link.target as NodeObject).id)
        : String(link.target);
    return `${s}|${link.edgeType}|${t}`;
  };

  /**
   * Force tuning: stronger repulsion, longer per-type link distances so
   * evidence nodes (research/procurement) sit farther out than first-order
   * entities, and a collision force that accounts for BOTH node radius and
   * approximate label dimensions so labels stop overlapping neighbors.
   */
  useEffect(() => {
    const graph = graphRef.current;
    if (!graph || typeof graph.d3Force !== 'function') return;
    graph.d3Force('charge')?.strength(-260).distanceMax(600);
    graph.d3Force('link')?.distance((link: FGLink) => {
      const targetId =
        typeof link.target === 'object'
          ? String((link.target as NodeObject).id)
          : String(link.target);
      const sourceId =
        typeof link.source === 'object'
          ? String((link.source as NodeObject).id)
          : String(link.source);
      const evidenceLink = [sourceId, targetId].some(
        (id) =>
          id.startsWith('research:') ||
          id.startsWith('procurement:') ||
          id.startsWith('cluster:'),
      );
      return evidenceLink ? 110 : 70;
    });
    graph.d3Force(
      'collide',
      forceCollide<NodeObject>((node) => {
        const fgNode = node as FGNode;
        const r =
          fgNode.kind === 'cluster'
            ? 18
            : nodeRadius(fgNode.nodeType, fgNode.recordCount, fgNode.isRoot);
        // Reserve room for the label below the node as well as beside it.
        return Math.max(r + 10, labelHalfWidth(fgNode.label, 10) * 0.7);
      }).iterations(2),
    );
  }, [ForceGraph2D, graphData]);

  // Remember node positions on every engine tick pause.
  const rememberPositions = useCallback(() => {
    graphData.nodes.forEach((node) => {
      if (typeof node.x === 'number' && typeof node.y === 'number') {
        positionsRef.current.set(node.id, { x: node.x, y: node.y });
      }
    });
  }, [graphData]);

  useEffect(() => {
    if (fitSignal !== undefined && graphRef.current) {
      graphRef.current.zoomToFit(400, 60);
    }
  }, [fitSignal]);

  const handleNodeClick = useCallback(
    (node: NodeObject) => {
      const fgNode = node as FGNode;
      const now = Date.now();
      const last = lastClickRef.current;
      lastClickRef.current = { id: fgNode.id as string, time: now };
      if (last.id === fgNode.id && now - last.time < DOUBLE_CLICK_MS) {
        onNodeExpand?.(fgNode.id as string);
        return;
      }
      onNodeSelect?.(fgNode.id as string);
    },
    [onNodeExpand, onNodeSelect],
  );

  const handleNodeRightClick = useCallback(
    (node: NodeObject, event: MouseEvent) => {
      event.preventDefault();
      setContextMenu({
        x: event.clientX,
        y: event.clientY,
        nodeId: (node as FGNode).id as string,
      });
    },
    [],
  );

  const paintNode = useCallback(
    (node: NodeObject, ctx: CanvasRenderingContext2D, globalScale: number) => {
      const fgNode = node as FGNode;
      const { x = 0, y = 0 } = fgNode;
      const meta = NODE_TYPE_META[fgNode.nodeType];
      const isCluster = fgNode.kind === 'cluster';
      const r = isCluster
        ? 12 + Math.min(6, Math.log2(1 + (fgNode.count ?? 0)))
        : nodeRadius(fgNode.nodeType, fgNode.recordCount, fgNode.isRoot);
      const isSelected = selectedNodeId === fgNode.id;
      const isHighlighted = highlightedNodeIds?.has(fgNode.id as string);
      const isNeighbor = neighborIds.has(fgNode.id as string);
      const isPinned = pinnedRef.current.has(fgNode.id as string);

      // Dim nodes unrelated to the current selection so direct relationships
      // become immediately obvious.
      const dimmed =
        Boolean(selectedNodeId) &&
        !isSelected &&
        !isNeighbor &&
        !isHighlighted &&
        !fgNode.isRoot;
      ctx.save();
      if (dimmed) ctx.globalAlpha = 0.25;

      // Halo for selection / highlight / root
      if (isSelected || isHighlighted || fgNode.isRoot) {
        let haloColor = 'rgba(255,215,0,0.25)';
        if (isSelected) haloColor = 'rgba(255,255,255,0.28)';
        else if (fgNode.isRoot) haloColor = `${meta.color}44`;
        ctx.save();
        drawShape(ctx, meta.shape, x, y, r + 4);
        ctx.fillStyle = haloColor;
        ctx.fill();
        ctx.restore();
      }

      ctx.save();
      drawShape(ctx, meta.shape, x, y, r);
      if (isCluster) {
        ctx.fillStyle = `${meta.color}33`;
        ctx.fill();
        ctx.setLineDash([3, 2]);
        ctx.strokeStyle = meta.color;
        ctx.lineWidth = 1.4 / globalScale;
        ctx.stroke();
        ctx.setLineDash([]);
      } else {
        ctx.fillStyle = meta.color;
        ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,0.75)';
        ctx.lineWidth = (isSelected ? 2 : 1) / globalScale;
        ctx.stroke();
      }
      ctx.restore();

      // Subtle pin indicator for manually-positioned nodes
      if (isPinned && !isCluster) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(
          x + r * 0.85,
          y - r * 0.85,
          Math.max(2.4, 2.4 / globalScale),
          0,
          2 * Math.PI,
        );
        ctx.fillStyle = '#facc15';
        ctx.fill();
        ctx.strokeStyle = 'rgba(0,0,0,0.6)';
        ctx.lineWidth = 0.6 / globalScale;
        ctx.stroke();
        ctx.restore();
      }

      // Cluster count badge
      if (isCluster) {
        ctx.font = `bold ${Math.max(10 / globalScale, 3.5)}px Sans-Serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = '#fff';
        ctx.fillText(String(fgNode.count ?? 0), x, y);
      }

      // Labels — declutter at low zoom
      const showLabel =
        isCluster ||
        fgNode.isRoot ||
        isSelected ||
        isNeighbor ||
        hoverNode?.id === fgNode.id ||
        globalScale >= LABEL_ZOOM_THRESHOLD;
      if (showLabel) {
        const fontSize = Math.max(
          (fgNode.isRoot || isCluster ? 12 : 10) / globalScale,
          2.6,
        );
        ctx.font = `${fgNode.isRoot ? 'bold ' : ''}${fontSize}px Sans-Serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        const label = truncate(fgNode.label);
        ctx.fillStyle = 'rgba(0,0,0,0.55)';
        const textWidth = ctx.measureText(label).width;
        ctx.fillRect(
          x - textWidth / 2 - 2,
          y + r + 2,
          textWidth + 4,
          fontSize + 2,
        );
        ctx.fillStyle = '#f8fafc';
        ctx.fillText(label, x, y + r + 3);
      }
      ctx.restore();
    },
    [selectedNodeId, highlightedNodeIds, hoverNode, neighborIds],
  );

  const contextNode = contextMenu
    ? nodes.find((n) => n.id === contextMenu.nodeId)
    : undefined;

  const closeContextMenu = () => setContextMenu(null);
  const runContextAction = (action?: (nodeId: string) => void) => {
    if (contextMenu && action) action(contextMenu.nodeId);
    closeContextMenu();
  };

  const togglePin = useCallback((nodeId: string) => {
    if (pinnedRef.current.has(nodeId)) {
      pinnedRef.current.delete(nodeId);
    } else {
      const pos = positionsRef.current.get(nodeId);
      if (pos) pinnedRef.current.set(nodeId, pos);
    }
    setPinnedVersion((v) => v + 1);
  }, []);

  /** Edge-type labels on hover and on selected-node links at useful zoom. */
  const paintLink = useCallback(
    (link: LinkObject, ctx: CanvasRenderingContext2D, globalScale: number) => {
      const fgLink = link as FGLink;
      const key = linkKey(fgLink);
      const isHovered = hoverLink && linkKey(hoverLink) === key;
      const isConnected = connectedLinkKeys.has(key);
      if (!isHovered && !(isConnected && globalScale >= EDGE_LABEL_ZOOM)) {
        return;
      }
      const source = fgLink.source as unknown as NodeObject;
      const target = fgLink.target as unknown as NodeObject;
      if (
        typeof source !== 'object' ||
        typeof target !== 'object' ||
        source.x === undefined ||
        target.x === undefined
      ) {
        return;
      }
      const mx = ((source.x ?? 0) + (target.x ?? 0)) / 2;
      const my = ((source.y ?? 0) + (target.y ?? 0)) / 2;
      const fontSize = Math.max(9 / globalScale, 2.4);
      ctx.save();
      ctx.font = `${fontSize}px Sans-Serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const text = fgLink.edgeType;
      const { width } = ctx.measureText(text);
      ctx.fillStyle = 'rgba(11,17,32,0.85)';
      ctx.fillRect(
        mx - width / 2 - 2,
        my - fontSize / 2 - 1,
        width + 4,
        fontSize + 2,
      );
      ctx.fillStyle = '#94a3b8';
      ctx.fillText(text, mx, my);
      ctx.restore();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hoverLink, connectedLinkKeys],
  );

  const zoomBy = (factor: number) => {
    const graph = graphRef.current;
    if (!graph) return;
    graph.zoom(graph.zoom() * factor, 250);
  };

  if (!ForceGraph2D) {
    return (
      <Box
        sx={{
          width,
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#0b1120',
        }}
      >
        <Typography variant="caption" sx={{ color: 'rgba(248,250,252,0.6)' }}>
          Loading graph…
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{ position: 'relative', width, height, overflow: 'hidden' }}
      onContextMenu={(e: ReactMouseEvent) => e.preventDefault()}
    >
      <ForceGraph2D
        ref={graphRef}
        width={width}
        height={height}
        graphData={graphData}
        backgroundColor="#0b1120"
        nodeId="id"
        nodeLabel={() => ''}
        nodeCanvasObject={paintNode}
        nodePointerAreaPaint={(node, color, ctx, globalScale) => {
          const fgNode = node as FGNode;
          const { x = 0, y = 0 } = fgNode;
          const r =
            fgNode.kind === 'cluster'
              ? 16
              : nodeRadius(fgNode.nodeType, fgNode.recordCount, fgNode.isRoot);
          ctx.fillStyle = color;
          // Forgiving hit area: noticeably larger than the visible node…
          ctx.beginPath();
          ctx.arc(x, y, Math.max(r + 6, 10), 0, 2 * Math.PI);
          ctx.fill();
          // …and the label is clickable too, selecting its node.
          const fontSize = Math.max(
            (fgNode.isRoot || fgNode.kind === 'cluster' ? 12 : 10) /
              (globalScale || 1),
            2.6,
          );
          const half = labelHalfWidth(fgNode.label, fontSize);
          ctx.fillRect(x - half, y + r + 1, half * 2, fontSize + 4);
        }}
        linkColor={(link) => {
          const fgLink = link as FGLink;
          const key = linkKey(fgLink);
          if (connectedLinkKeys.has(key)) return 'rgba(56,189,248,0.95)';
          if (selectedNodeId) return 'rgba(148,163,184,0.18)';
          return fgLink.relationshipType === 'derived'
            ? 'rgba(148,163,184,0.45)'
            : 'rgba(148,163,184,0.85)';
        }}
        linkWidth={(link) => {
          const fgLink = link as FGLink;
          if (connectedLinkKeys.has(linkKey(fgLink))) return 2.2;
          return fgLink.relationshipType === 'derived' ? 1 : 1.6;
        }}
        linkLineDash={(link) =>
          (link as FGLink).relationshipType === 'derived' ? [4, 3] : null
        }
        linkCanvasObjectMode={() => 'after'}
        linkCanvasObject={paintLink}
        linkDirectionalParticles={0}
        onNodeClick={handleNodeClick}
        onNodeRightClick={handleNodeRightClick}
        onNodeHover={(node) => setHoverNode((node as FGNode) ?? null)}
        onLinkHover={(link) => setHoverLink((link as FGLink) ?? null)}
        onLinkClick={(link) => onLinkSelect?.(link as FGLink)}
        onBackgroundClick={() => {
          onNodeSelect?.(null);
          closeContextMenu();
        }}
        onNodeDrag={(node) => {
          // Pin the node while dragging so the simulation never fights the
          // analyst; neighbors stay relatively stable.
          const fgNode = node as FGNode;
          fgNode.fx = fgNode.x;
          fgNode.fy = fgNode.y;
        }}
        onNodeDragEnd={(node) => {
          const fgNode = node as FGNode;
          if (typeof fgNode.x === 'number' && typeof fgNode.y === 'number') {
            // Retain the manually-selected position: keep the node pinned.
            fgNode.fx = fgNode.x;
            fgNode.fy = fgNode.y;
            positionsRef.current.set(fgNode.id as string, {
              x: fgNode.x,
              y: fgNode.y,
            });
            pinnedRef.current.set(fgNode.id as string, {
              x: fgNode.x,
              y: fgNode.y,
            });
          }
        }}
        onEngineStop={rememberPositions}
        onZoom={({ k }) => setZoomLevel(k)}
        cooldownTicks={140}
        d3AlphaDecay={0.05}
        d3VelocityDecay={0.4}
        minZoom={0.3}
        maxZoom={10}
      />

      {/* Zoom / fit controls */}
      <Paper
        elevation={3}
        sx={{
          position: 'absolute',
          right: 12,
          bottom: 12,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
        }}
      >
        <Tooltip title="Zoom in" placement="left">
          <IconButton size="small" onClick={() => zoomBy(1.4)}>
            <Icon icon="mdi:plus" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom out" placement="left">
          <IconButton size="small" onClick={() => zoomBy(1 / 1.4)}>
            <Icon icon="mdi:minus" />
          </IconButton>
        </Tooltip>
        <Divider />
        <Tooltip title="Fit graph" placement="left">
          <IconButton
            size="small"
            onClick={() => graphRef.current?.zoomToFit(400, 60)}
          >
            <Icon icon="mdi:fit-to-screen-outline" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Recenter" placement="left">
          <IconButton
            size="small"
            onClick={() => graphRef.current?.centerAt(0, 0, 400)}
          >
            <Icon icon="mdi:image-filter-center-focus" />
          </IconButton>
        </Tooltip>
      </Paper>

      {/* Hover tooltip */}
      {hoverNode && (
        <Paper
          elevation={4}
          sx={{
            position: 'absolute',
            left: 12,
            bottom: 12,
            px: 1.5,
            py: 1,
            maxWidth: 340,
            pointerEvents: 'none',
            borderLeft: `3px solid ${NODE_TYPE_META[hoverNode.nodeType].color}`,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Icon
              icon={NODE_TYPE_META[hoverNode.nodeType].icon}
              color={NODE_TYPE_META[hoverNode.nodeType].color}
            />
            <Typography variant="subtitle2" sx={{ wordBreak: 'break-word' }}>
              {hoverNode.label}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" component="div">
            {hoverNode.kind === 'cluster'
              ? `${NODE_TYPE_META[hoverNode.nodeType].label} cluster · ${
                  hoverNode.count
                } entities — click to expand`
              : `${NODE_TYPE_META[hoverNode.nodeType].label} · ${
                  hoverNode.connectionCount
                } relationships · ${
                  hoverNode.evidenceCount
                } evidence records · ${datasetSummary(hoverNode.datasets)}`}
          </Typography>
        </Paper>
      )}

      {/* Zoom indicator */}
      <Typography
        variant="caption"
        sx={{
          position: 'absolute',
          right: 16,
          top: 8,
          color: 'rgba(248,250,252,0.5)',
        }}
      >
        {Math.round(zoomLevel * 100)}%
      </Typography>

      {/* Right-click context menu */}
      <Menu
        open={Boolean(contextMenu)}
        onClose={closeContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu ? { top: contextMenu.y, left: contextMenu.x } : undefined
        }
      >
        <MenuItem
          disabled={contextNode?.kind === 'cluster'}
          onClick={() => runContextAction(contextActions?.onExpand)}
        >
          <ListItemIcon>
            <Icon icon="mdi:arrow-expand-all" />
          </ListItemIcon>
          <ListItemText>Expand</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => runContextAction(contextActions?.onFocus)}>
          <ListItemIcon>
            <Icon icon="mdi:image-filter-center-focus" />
          </ListItemIcon>
          <ListItemText>Focus</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => runContextAction(contextActions?.onHide)}>
          <ListItemIcon>
            <Icon icon="mdi:eye-off-outline" />
          </ListItemIcon>
          <ListItemText>Hide</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={contextNode?.kind === 'cluster'}
          onClick={() => runContextAction(togglePin)}
        >
          <ListItemIcon>
            <Icon
              icon={
                contextMenu && pinnedRef.current.has(contextMenu.nodeId)
                  ? 'mdi:pin-off-outline'
                  : 'mdi:pin-outline'
              }
            />
          </ListItemIcon>
          <ListItemText>
            {contextMenu && pinnedRef.current.has(contextMenu.nodeId)
              ? 'Unpin Node'
              : 'Pin Node'}
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => runContextAction(contextActions?.onFindConnections)}
        >
          <ListItemIcon>
            <Icon icon="mdi:vector-polyline" />
          </ListItemIcon>
          <ListItemText>Find Connections</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => runContextAction(contextActions?.onViewEvidence)}
        >
          <ListItemIcon>
            <Icon icon="mdi:file-search-outline" />
          </ListItemIcon>
          <ListItemText>View Evidence</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => runContextAction(contextActions?.onOpenSource)}
        >
          <ListItemIcon>
            <Icon icon="mdi:open-in-new" />
          </ListItemIcon>
          <ListItemText>Open Source Record</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};
